import { ERROR_CODES } from "@bright/shared";

/**
 * The one way this application talks to the server.
 *
 * Everything the API can say is either a JSON body or the error envelope
 * `{ error: { code, message, details? } }` — this client turns the second into a
 * thrown `ApiError` so callers never branch on `res.ok`. A 401 on an access token
 * that has simply expired is handled once, transparently: refresh, then replay the
 * original request. A second 401 means the session is genuinely over.
 */

import { getActiveBook, setActiveBook, type BookKey } from "./book";

/** The server's refusal of a book this session may not open (auth plugin). */
const BOOK_SHUT = "That book is not linked to yours.";

export const API_BASE_URL = "/api/v1";

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details?: unknown;

  constructor(args: {
    code: string;
    message: string;
    status: number;
    details?: unknown;
  }) {
    super(args.message);
    this.name = "ApiError";
    this.code = args.code;
    this.status = args.status;
    this.details = args.details;
  }

  /** True when the failure is worth showing a retry button for. */
  get isRetryable(): boolean {
    return (
      this.status === 0 ||
      this.status === 408 ||
      this.status === 429 ||
      this.status >= 500
    );
  }

  get isAuthFailure(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }
}

// ---------------------------------------------------------------------------
// Token plumbing
//
// The auth provider owns the tokens; the client owns the transport. They meet
// through these setters rather than an import, which keeps the module graph acyclic
// and means a token refresh never re-renders the tree.
// ---------------------------------------------------------------------------

type RefreshFn = () => Promise<string | null>;

let accessToken: string | null = null;
let refreshFn: RefreshFn | null = null;
let onSessionExpired: (() => void) | null = null;
let inFlightRefresh: Promise<string | null> | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function setRefreshHandler(fn: RefreshFn | null): void {
  refreshFn = fn;
}

export function setSessionExpiredHandler(fn: (() => void) | null): void {
  onSessionExpired = fn;
}

/** Told when the server shuts a book this session sent — a cover that has ended (20 Oct 2026). */
let onBookShut: (() => void) | null = null;
export function setBookShutHandler(fn: (() => void) | null): void {
  onBookShut = fn;
}

/** Concurrent 401s share a single refresh rather than racing five of them. */
async function refreshOnce(): Promise<string | null> {
  if (!refreshFn) return null;
  if (!inFlightRefresh) {
    inFlightRefresh = refreshFn().finally(() => {
      inFlightRefresh = null;
    });
  }
  return inFlightRefresh;
}

// ---------------------------------------------------------------------------
// Query strings
// ---------------------------------------------------------------------------

export type QueryValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | Array<string | number | boolean>;
export type QueryParams = Record<string, QueryValue>;

/**
 * Serialises the shared filter vocabulary. Arrays repeat the key (`status=A&status=B`)
 * because that is what the zod array schemas on the server accept; empty strings,
 * nulls and empty arrays are dropped so a cleared filter does not become `?model=`.
 */
export function buildQueryString(params?: QueryParams): string {
  if (!params) return "";
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    if (Array.isArray(value)) {
      for (const item of value) {
        if (item === undefined || item === null || item === "") continue;
        search.append(key, String(item));
      }
    } else {
      search.append(key, String(value));
    }
  }
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

function joinUrl(path: string, params?: QueryParams): string {
  const normalised = path.startsWith("/") ? path : `/${path}`;
  return `${API_BASE_URL}${normalised}${buildQueryString(params)}`;
}

// ---------------------------------------------------------------------------
// Core request
// ---------------------------------------------------------------------------

export interface RequestOptions {
  params?: QueryParams;
  signal?: AbortSignal;
  headers?: Record<string, string>;
  /**
   * Which of a company's two lines answers: "tvs" or "bow" (8 Sep 2026). Null
   * pins the session's own book even while another is active in the header;
   * undefined follows the active book.
   */
  book?: BookKey | null;
  /** Skip the bearer header — used by login and refresh. */
  anonymous?: boolean;
  /** Explicit timeout budget in milliseconds (defaults to 25s for JSON, up to 10m for uploads). */
  timeoutMs?: number;
  /** Internal: prevents a refreshed request from refreshing again. */
  _retried?: boolean;
}

interface ErrorEnvelope {
  error?: { code?: string; message?: string; details?: unknown };
}

async function readError(res: Response): Promise<ApiError> {
  let code = ERROR_CODES.INTERNAL as string;
  let message = "Something went wrong. Please try again.";
  let details: unknown;

  try {
    const body = (await res.json()) as ErrorEnvelope;
    if (body && typeof body === "object" && body.error) {
      code = body.error.code ?? code;
      message = body.error.message ?? message;
      details = body.error.details;
    }
  } catch {
    // A gateway or proxy answered with HTML, or the body was empty. Fall back to
    // a status-shaped message rather than surfacing a JSON parse error.
    message = res.statusText || message;
  }

  if (res.status === 401) {
    code = ERROR_CODES.UNAUTHORIZED;
    if (!message || message === res.statusText)
      message = "Your session has expired.";
  } else if (res.status === 403) {
    code = ERROR_CODES.FORBIDDEN;
    if (!message || message === res.statusText) {
      message = "You do not have permission to do that.";
    }
  } else if (res.status === 404 && (!message || message === res.statusText)) {
    code = ERROR_CODES.NOT_FOUND;
    message = "That record could not be found.";
  } else if (res.status === 413 && (!message || message === res.statusText || message === "Something went wrong. Please try again.")) {
    // The server's front door refusing a file too big to read: its page is
    // HTML with no message, and over HTTP/2 not even a status text (3 Oct 2026).
    message = "That file is too big to send — keep it under 12 MB (a PDF scanned at a lower quality, or fewer pages).";
  }

  return new ApiError({ code, message, status: res.status, details });
}

/** The bytes of the files in an upload. */
function formDataBytes(body: FormData): number {
  let bytes = 0;
  body.forEach((value) => {
    if (typeof value !== "string") bytes += value.size;
  });
  return bytes;
}

async function rawRequest(
  method: string,
  path: string,
  body: unknown,
  options: RequestOptions,
): Promise<Response> {
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...options.headers,
  };
  const book = options.book === undefined ? getActiveBook() : options.book;
  if (book && !options.anonymous) headers["X-Book"] = book;

  const isFormData =
    typeof FormData !== "undefined" && body instanceof FormData;
  if (body !== undefined && !isFormData)
    headers["Content-Type"] = "application/json";
  if (!options.anonymous && accessToken)
    headers.Authorization = `Bearer ${accessToken}`;

  /*
   * A ceiling on waiting. Without one, a request that the network swallows
   * never settles: the screen keeps its spinner, react-query never retries,
   * and the counter is left looking at an app that appears to have stopped —
   * with no error and nothing to tap. Uploads get longer, because a photo on a
   * shop's 4G legitimately takes a while.
   */
  // An upload also gets time for its size (3 Oct 2026): photos are shrunk to a
  // few hundred KB, but a PDF goes up whole, and 8 MB on a shop's 1 Mbps uplink
  // is more than a minute on its own. 20 s a megabyte on top, ten minutes at most.
  const budget = options.timeoutMs ?? (isFormData ? Math.min(90_000 + 20_000 * Math.ceil(formDataBytes(body as FormData) / 1_048_576), 600_000) : 25_000);
  const timeout = AbortSignal.timeout(budget);
  const signal = options.signal ? AbortSignal.any([options.signal, timeout]) : timeout;

  return fetch(joinUrl(path, options.params), {
    method,
    headers,
    credentials: "same-origin",
    signal,
    body:
      body === undefined ? undefined : isFormData ? body : JSON.stringify(body),
  });
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  options: RequestOptions = {},
): Promise<T> {
  // The book this request is sent from, as rawRequest will send it.
  const sentBook = options.anonymous ? null : options.book === undefined ? getActiveBook() : options.book;
  let res: Response;
  try {
    res = await rawRequest(method, path, body, options);
  } catch (err) {
    // A timeout aborts too, but it is not the caller cancelling — it deserves
    // its own words, because "check your connection" is the useful advice and
    // silence is not.
    if (err instanceof DOMException && err.name === "TimeoutError") {
      throw new ApiError({
        code: "NETWORK_TIMEOUT",
        message: "The server did not answer. Check your connection and try again.",
        status: 0,
      });
    }
    if (err instanceof DOMException && err.name === "AbortError") throw err;
    throw new ApiError({
      code: "NETWORK_ERROR",
      message:
        "Could not reach the server. Check your connection and try again.",
      status: 0,
      details: err instanceof Error ? err.message : undefined,
    });
  }

  if (res.status === 401 && !options.anonymous && !options._retried) {
    const token = await refreshOnce();
    if (token) {
      return request<T>(method, path, body, { ...options, _retried: true });
    }
    onSessionExpired?.();
    throw await readError(res);
  }

  if (!res.ok) {
    const error = await readError(res);
    // A book this session may no longer open — a cover that has ended (20 Oct
    // 2026): back to its own book, so the next screen asks the right one.
    if (res.status === 403 && error.message === BOOK_SHUT && sentBook) {
      if (getActiveBook() === sentBook) setActiveBook(null);
      onBookShut?.();
    }
    throw error;
  }

  if (res.status === 204 || res.headers.get("Content-Length") === "0") {
    return undefined as T;
  }

  const contentType = res.headers.get("Content-Type") ?? "";
  if (!contentType.includes("json")) {
    return (await res.text()) as unknown as T;
  }

  return (await res.json()) as T;
}

/**
 * A POST whose answer is read as it arrives (the CEO's chat, 26 Oct 2026): the
 * response itself, once it is known to be good, with the same session refresh
 * and error words as every other request. Five minutes at most.
 */
export async function openStream(path: string, body: unknown, options: RequestOptions = {}): Promise<Response> {
  let res: Response;
  try {
    res = await rawRequest("POST", path, body, { timeoutMs: 300_000, ...options, headers: { Accept: "application/x-ndjson", ...options.headers } });
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") throw err;
    throw new ApiError({
      code: "NETWORK_ERROR",
      message: "Could not reach the server. Check your connection and try again.",
      status: 0,
      details: err instanceof Error ? err.message : undefined,
    });
  }
  if (res.status === 401 && !options._retried) {
    if (await refreshOnce()) return openStream(path, body, { ...options, _retried: true });
    onSessionExpired?.();
  }
  if (!res.ok) throw await readError(res);
  return res;
}

export const api = {
  get: <T>(path: string, params?: QueryParams, options: RequestOptions = {}) =>
    request<T>("GET", path, undefined, {
      ...options,
      params: params ?? options.params,
    }),
  post: <T>(path: string, body?: unknown, options: RequestOptions = {}) =>
    request<T>("POST", path, body, options),
  put: <T>(path: string, body?: unknown, options: RequestOptions = {}) =>
    request<T>("PUT", path, body, options),
  patch: <T>(path: string, body?: unknown, options: RequestOptions = {}) =>
    request<T>("PATCH", path, body, options),
  delete: <T>(path: string, options: RequestOptions = {}) =>
    request<T>("DELETE", path, undefined, options),
};

// ---------------------------------------------------------------------------
// Binary downloads (report exports)
// ---------------------------------------------------------------------------

/**
 * Pulls a file through the authenticated client and hands it to the browser.
 *
 * A plain `<a href>` would drop the bearer token, and a signed-URL round trip would
 * put filter parameters somewhere they could be shared by accident, so the bytes come
 * back over fetch and are handed over as an object URL. If the server answers with the
 * error envelope instead of a file, that is surfaced as a normal `ApiError` — the user
 * sees "no rows matched", not a corrupt spreadsheet.
 */
/**
 * Opens a protected file in a new tab — a sale paper, a photo — without ever
 * putting the bearer token in a URL. Same fetch path as `downloadFile`; the
 * only difference is that the bytes are shown rather than saved. The object
 * URL is revoked once the new tab has had a moment to load it.
 */
/**
 * Fetch a file through the authenticated client and hand back an object URL
 * for an <img> or <iframe>. The caller revokes it when done.
 */
export async function fetchFileUrl(
  path: string,
  options: RequestOptions = {},
): Promise<string> {
  let res: Response;
  try {
    res = await rawRequest("GET", path, undefined, options);
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") throw err;
    throw new ApiError({
      code: "NETWORK_ERROR",
      message:
        "Could not reach the server. Check your connection and try again.",
      status: 0,
    });
  }
  if (res.status === 401 && !options._retried) {
    const token = await refreshOnce();
    if (token) return fetchFileUrl(path, { ...options, _retried: true });
    onSessionExpired?.();
  }
  if (!res.ok) throw await readError(res);
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

/** Open a file in a new tab (falls back to this tab under a popup blocker). */
export async function openFile(
  path: string,
  options: RequestOptions = {},
): Promise<void> {
  const url = await fetchFileUrl(path, options);
  const tab = window.open(url, "_blank", "noopener");
  if (!tab) {
    // A popup blocker swallowed it: fall back to navigating this tab.
    window.location.assign(url);
  }
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export async function downloadFile(
  path: string,
  filename: string,
  options: RequestOptions = {},
): Promise<void> {
  let res: Response;
  try {
    res = await rawRequest("GET", path, undefined, options);
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") throw err;
    throw new ApiError({
      code: "NETWORK_ERROR",
      message:
        "Could not reach the server. Check your connection and try again.",
      status: 0,
    });
  }

  if (res.status === 401 && !options._retried) {
    const token = await refreshOnce();
    if (token)
      return downloadFile(path, filename, { ...options, _retried: true });
    onSessionExpired?.();
  }

  if (!res.ok) throw await readError(res);

  const blob = await res.blob();
  if (blob.size === 0) {
    throw new ApiError({
      code: ERROR_CODES.INTERNAL,
      message: "The export came back empty. Please try again.",
      status: res.status,
    });
  }

  // Prefer the server's own filename when it sent one — it knows the period and format.
  const disposition = res.headers.get("Content-Disposition") ?? "";
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  const resolved = match?.[1] ? decodeURIComponent(match[1]) : filename;

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = resolved;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Revoke on the next tick; revoking synchronously cancels the download in Safari.
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Turns anything thrown by a query into copy a person can act on. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return "Something went wrong. Please try again.";
}
